import { useState, useEffect } from "react";
import { supabase } from "../supabaseClient";
import { T, THEMES } from "../themes";
import { CheckCircle, WarningCircle, ChatCentered } from "@phosphor-icons/react";
import AppHeader from "../components/AppHeader";

// Alias helpers — read from mutable T singleton at call time (dark mode safe)
function getLabelStyle() {
  return {
    color: T.textDim,
    fontSize: 13,
    fontFamily: T.fontSans,
    display: "block",
    marginBottom: 8,
    fontWeight: 600,
    letterSpacing: "0.01em",
  };
}

function fieldBase(focused) {
  return {
    width: "100%",
    backgroundColor: T.surfaceLight,
    border: focused ? `1.5px solid ${T.accent}` : `1px solid ${T.cardBorder}`,
    boxShadow: focused
      ? `inset 3px 0 0 ${T.accent}, 0 0 0 3px ${T.accent}1a`
      : "none",
    padding: "12px 16px",
    color: T.ink,
    fontSize: 14,
    fontFamily: T.fontSans,
    fontWeight: 500,
    outline: "none",
    boxSizing: "border-box",
    borderRadius: T.radiusXs,
    transition: "border-color 0.2s, box-shadow 0.2s",
  };
}

function submitBtnStyle(disabled, hovered) {
  return {
    width: "100%",
    minHeight: 52,
    backgroundColor: disabled ? T.textDim : hovered ? T.primaryDark : T.accent,
    color: disabled ? T.surfaceLight : "#fff",
    border: "none",
    borderRadius: T.radiusSm,
    padding: "0 22px",
    fontSize: 15,
    fontWeight: 700,
    fontFamily: T.fontSans,
    letterSpacing: "0.01em",
    cursor: disabled ? "default" : "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 7,
    transition: "background-color 0.15s",
  };
}

const CATEGORIES = [
  { value: "bug",     label: "🐛  Bug" },
  { value: "feature", label: "💡  Feature Request" },
  { value: "food",    label: "🍎  Missing Food or Exercise" },
  { value: "ui",      label: "🎨  UI Feedback" },
];

const SEVERITY_OPTIONS = [
  { value: "low",      label: "Low — minor annoyance" },
  { value: "medium",   label: "Medium — affects usability" },
  { value: "high",     label: "High — blocks a key feature" },
  { value: "critical", label: "Critical — app crashes / data lost" },
];

const APP_OPTIONS = [
  { value: "nutrilithe", label: "NutriLithe" },
  { value: "liftlithe",  label: "LiftLithe" },
  { value: "both",       label: "Both apps" },
];

const INITIAL_FORM = {
  app: "",
  category: "",
  severity: "",
  description: "",
  device_info: "",
};

// ── Select ────────────────────────────────────────────────────────────────────
function Select({ label, value, onChange, options, placeholder }) {
  const [focused, setFocused] = useState(false);
  return (
    <div style={{ width: "100%", marginBottom: 20 }}>
      {label && <label style={getLabelStyle()}>{label}</label>}
      <select
        value={value}
        onChange={onChange}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        style={{
          ...fieldBase(focused),
          color: value ? T.ink : T.textDim,
          appearance: "none",
          WebkitAppearance: "none",
          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%23888' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E")`,
          backgroundRepeat: "no-repeat",
          backgroundPosition: "right 14px center",
          backgroundColor: T.surfaceLight,
          paddingRight: 38,
        }}
      >
        <option value="" disabled>{placeholder}</option>
        {options.map(o => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </div>
  );
}

// ── Field ─────────────────────────────────────────────────────────────────────
function Field({ label, value, onChange, placeholder, textarea }) {
  const [focused, setFocused] = useState(false);
  return (
    <div style={{ width: "100%", marginBottom: 20 }}>
      {label && <label style={getLabelStyle()}>{label}</label>}
      {textarea
        ? <textarea value={value} onChange={onChange} placeholder={placeholder} rows={5}
            onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
            style={{ ...fieldBase(focused), resize: "vertical", lineHeight: 1.6 }} />
        : <input value={value} onChange={onChange} placeholder={placeholder}
            onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
            style={fieldBase(focused)} />
      }
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function FeedbackPage() {
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem("np_dark") === "true");
  const [session, setSession]       = useState(undefined);
  const [form, setForm]             = useState(INITIAL_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted]   = useState(false);
  const [error, setError]           = useState("");
  const [btnHovered, setBtnHovered] = useState(false);

  // Apply theme at top of render so T.* reads are correct for this render pass
  Object.assign(T, THEMES[darkMode ? "BalancedDark" : "Balanced"] || THEMES.Balanced);
  // Sync body background so the browser viewport matches on theme toggle / navigation arrival
  document.body.style.backgroundColor = T.pageBg;

  // Computed page-level styles (must be after Object.assign)
  const pageStyle = {
    minHeight: "100vh",
    backgroundColor: T.pageBg,
    fontFamily: T.fontSans,
  };
  const cardStyle = {
    backgroundColor: T.card,
    border: `1px solid ${T.cardBorder}`,
    borderRadius: T.radius,
    padding: 24,
    boxShadow: T.shadowXs,
  };

  function handleToggleDarkMode() {
    const next = !darkMode;
    setDarkMode(next);
    localStorage.setItem("np_dark", String(next));
  }

  // Resolve auth session once on mount
  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setSession(data?.session ?? null);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s);
    });
    return () => subscription.unsubscribe();
  }, []);

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const canSubmit =
    !submitting &&
    form.app &&
    form.category &&
    form.description.trim().length >= 20 &&
    (form.category !== "bug" || form.severity);

  // Progress indicator
  const requiredFields = [
    !!form.app,
    !!form.category,
    ...(form.category === "bug" ? [!!form.severity] : []),
    form.description.trim().length >= 20,
  ];
  const filledCount = requiredFields.filter(Boolean).length;
  const totalCount  = requiredFields.length;

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const { error: err } = await supabase.from("feedback").insert({
        user_id:     session.user.id,
        app:         form.app,
        category:    form.category,
        severity:    form.category === "bug" ? (form.severity || null) : null,
        description: form.description.trim(),
        device_info: form.device_info.trim() || null,
      });
      if (err) throw err;
      setSubmitted(true);
    } catch (err) {
      setError(err.message || "Submission failed — please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  // ── Loading ──────────────────────────────────────────────────────────────────
  if (session === undefined) {
    return (
      <div style={pageStyle}>
        <AppHeader title="Feedback" subtitle="Help us improve" currentApp="nutri"
          darkMode={darkMode} onToggleDarkMode={handleToggleDarkMode} />
        <div style={{ color: T.textDim, fontFamily: T.fontSans, fontSize: 14, padding: 32, textAlign: "center" }}>
          Loading…
        </div>
      </div>
    );
  }

  // ── Not logged in ────────────────────────────────────────────────────────────
  if (!session) {
    return (
      <div style={pageStyle}>
        <AppHeader title="Feedback" subtitle="Help us improve" currentApp="nutri"
          darkMode={darkMode} onToggleDarkMode={handleToggleDarkMode} />
        <div style={{ maxWidth: 440, margin: "0 auto", padding: "32px 20px 60px" }}>
          <div style={{ ...cardStyle, textAlign: "center", padding: "40px 28px" }}>
            <div style={{
              width: 56, height: 56, borderRadius: "50%",
              backgroundColor: T.surfaceLight, display: "flex", alignItems: "center",
              justifyContent: "center", margin: "0 auto 20px",
            }}>
              <ChatCentered size={26} color={T.textDim} />
            </div>
            <h2 style={{ color: T.ink, fontFamily: T.fontDisplay, fontSize: 20, fontWeight: 700, margin: "0 0 10px" }}>
              Sign in required
            </h2>
            <p style={{ color: T.textDim, fontFamily: T.fontSans, fontSize: 14, lineHeight: 1.6, margin: "0 0 28px" }}>
              Please log in to your Lithe.Fit account first, then return to this page to leave feedback.
            </p>
            <a href="/" style={{
              display: "inline-block",
              backgroundColor: T.accent, color: "#fff",
              fontFamily: T.fontSans, fontWeight: 700, fontSize: 14,
              padding: "13px 28px", borderRadius: T.radiusSm, textDecoration: "none",
            }}>
              Go to Lithe.Fit
            </a>
          </div>
        </div>
      </div>
    );
  }

  // ── Success ──────────────────────────────────────────────────────────────────
  if (submitted) {
    return (
      <div style={pageStyle}>
        <AppHeader title="Feedback" subtitle="Help us improve" currentApp="nutri"
          darkMode={darkMode} onToggleDarkMode={handleToggleDarkMode} />
        <div style={{ maxWidth: 440, margin: "0 auto", padding: "32px 20px 60px" }}>
          <div style={{ ...cardStyle, textAlign: "center", padding: "40px 28px" }}>
            <div style={{
              width: 64, height: 64, borderRadius: "50%",
              backgroundColor: `${T.success}22`, display: "flex", alignItems: "center",
              justifyContent: "center", margin: "0 auto 20px",
            }}>
              <CheckCircle size={32} color={T.success} weight="fill" />
            </div>
            <h2 style={{ color: T.ink, fontFamily: T.fontDisplay, fontSize: 22, fontWeight: 700, margin: "0 0 10px" }}>
              Thanks for the feedback!
            </h2>
            <p style={{ color: T.textDim, fontFamily: T.fontSans, fontSize: 14, lineHeight: 1.6, margin: "0 0 32px" }}>
              We read every submission. Your input shapes what gets built next.
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <button
                onClick={() => { setSubmitted(false); setForm(INITIAL_FORM); }}
                style={submitBtnStyle(false, true)}
              >
                Submit another
              </button>
              <a href="/" style={{ color: T.textDim, fontFamily: T.fontSans, fontSize: 13, textDecoration: "none" }}>
                ← Back to app
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Form ─────────────────────────────────────────────────────────────────────
  return (
    <div style={pageStyle}>
      <AppHeader title="Feedback" subtitle="Help us improve" currentApp="nutri"
        darkMode={darkMode} onToggleDarkMode={handleToggleDarkMode} />
      <div style={{ maxWidth: 520, margin: "0 auto", padding: "32px 20px 60px" }}>

        {/* Form card */}
        <div style={cardStyle}>

          {/* Header */}
          <div style={{ marginBottom: 28 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
              <ChatCentered size={15} color={T.accent} />
              <span style={{ color: T.accent, fontFamily: T.fontSans, fontSize: 11, fontWeight: 700, letterSpacing: "0.3px" }}>
                Beta feedback
              </span>
            </div>
            <h1 style={{ color: T.ink, fontFamily: T.fontDisplay, fontSize: 22, fontWeight: 800, margin: "0 0 8px", lineHeight: 1.25 }}>
              Help us make Lithe.Fit better
            </h1>
            <p style={{ color: T.textDim, fontFamily: T.fontSans, fontSize: 13, margin: "0 0 8px", lineHeight: 1.55 }}>
              Your feedback goes directly to the team. Be as specific as you can.
            </p>
            <p style={{ color: T.textDim, fontFamily: T.fontSans, fontSize: 12, margin: 0 }}>
              Logged in as <span style={{ color: T.textSub, fontWeight: 600 }}>{session.user.email}</span>
            </p>
          </div>

          {/* Fields */}
          <form onSubmit={handleSubmit}>

            <Select
              label="Which app?"
              value={form.app}
              onChange={e => set("app", e.target.value)}
              options={APP_OPTIONS}
              placeholder="Select app…"
            />

            <Select
              label="Category"
              value={form.category}
              onChange={e => { set("category", e.target.value); set("severity", ""); }}
              options={CATEGORIES}
              placeholder="Select category…"
            />

            {form.category === "bug" && (
              <Select
                label="Severity"
                value={form.severity}
                onChange={e => set("severity", e.target.value)}
                options={SEVERITY_OPTIONS}
                placeholder="How bad is it?"
              />
            )}

            <Field
              label="Description *"
              value={form.description}
              onChange={e => set("description", e.target.value)}
              placeholder="Describe the issue or idea in detail. Minimum 20 characters."
              textarea
            />
            {form.description.length > 0 && form.description.trim().length < 20 && (
              <p style={{ color: T.textDim, fontFamily: T.fontSans, fontSize: 11, margin: "-12px 0 20px", padding: "0 2px" }}>
                {20 - form.description.trim().length} more characters needed
              </p>
            )}

            <Field
              label="Device (optional)"
              value={form.device_info}
              onChange={e => set("device_info", e.target.value)}
              placeholder="e.g. Samsung Galaxy S23, Android 14"
            />

            {error && (
              <div style={{
                display: "flex", alignItems: "flex-start", gap: 8,
                backgroundColor: `${T.danger}18`, border: `1px solid ${T.danger}44`,
                borderRadius: T.radiusXs, padding: "10px 14px", marginBottom: 20,
              }}>
                <WarningCircle size={16} color={T.danger} style={{ flexShrink: 0, marginTop: 1 }} />
                <span style={{ color: T.danger, fontFamily: T.fontSans, fontSize: 13, lineHeight: 1.4 }}>{error}</span>
              </div>
            )}

            {/* Progress indicator */}
            <p style={{
              color: filledCount === totalCount ? T.success : T.accent,
              fontFamily: T.fontSans,
              fontSize: 12,
              margin: "0 0 12px",
              fontWeight: filledCount === totalCount ? 700 : 500,
              transition: "color 0.2s",
            }}>
              {filledCount === totalCount
                ? "✓ All required fields complete"
                : `${filledCount} of ${totalCount} required fields complete`}
            </p>

            {/* Submit button */}
            <button
              type="submit"
              disabled={!canSubmit}
              onMouseEnter={() => setBtnHovered(true)}
              onMouseLeave={() => setBtnHovered(false)}
              style={submitBtnStyle(!canSubmit, btnHovered)}
            >
              {submitting ? "Sending…" : "Submit feedback"}
            </button>

            <p style={{ color: T.textDim, fontFamily: T.fontSans, fontSize: 11, textAlign: "center", marginTop: 16, lineHeight: 1.5 }}>
              Feedback is linked to your account and reviewed by the team.
              <br />
              <a href="/" style={{ color: T.textDim }}>← Back to app</a>
            </p>

          </form>
        </div>
      </div>
    </div>
  );
}
